import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// Molto semplice: converte **bold**, `code`, elenchi in HTML minimo.
const renderMarkdown = (txt) => {
  if (!txt) return "";
  let out = txt
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/`([^`]+)`/g, '<code class="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">$1</code>')
    .replace(/^&gt; (.+)$/gm, '<div class="mt-1 border-l-2 border-blue-300 bg-blue-50 pl-2 text-blue-900">$1</div>')
    .replace(/\n/g, "<br>");
  return out;
};

export default function AssistantWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role;
  const canUse = role === "admin" || role === "assistente";

  const WELCOME_MSG = { role: "assistant", content: t("assistant.welcome") };

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset welcome message when language changes
  useEffect(() => {
    if (messages.length === 1) {
      setMessages([{ role: "assistant", content: t("assistant.welcome") }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  if (!canUse) return null;

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile contattare l'assistente");
      setMessages((m) => [...m, { role: "assistant", content: t("assistant.error_generic") }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = [
    t("assistant.suggestion1", "Come importo un Excel di appuntamenti?"),
    t("assistant.suggestion2", "Come genero un BEF di fine mese?"),
    t("assistant.suggestion3", "Come firmo una bolla DDT?"),
    t("assistant.suggestion4", "Come creo un nuovo utente?"),
  ];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-gradient-to-br from-[#0052FF] to-[#0033AA] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl"
          data-testid="ai-assistant-toggle"
        >
          <Sparkles className="h-4 w-4" />
          {t("assistant.help_button")}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-40 flex h-[600px] max-h-[calc(100vh-40px)] w-[95vw] max-w-[420px] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-[#0052FF] to-[#0033AA] p-3 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <div>
                <div className="text-sm font-semibold">{t("assistant.title")}</div>
                <div className="text-[10px] opacity-80">{t("assistant.subtitle")}</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded p-1 hover:bg-white/10"
              data-testid="ai-assistant-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-3 text-sm">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 ${
                    m.role === "user"
                      ? "bg-[#0052FF] text-white"
                      : "border border-slate-200 bg-white text-slate-800"
                  }`}
                  data-testid={`ai-msg-${m.role}-${i}`}
                >
                  {m.role === "assistant" ? (
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} className="leading-relaxed" />
                  ) : (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" /> {t("assistant.writing")}
                </div>
              </div>
            )}
            {messages.length === 1 && !sending && (
              <div className="pt-1">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t("assistant.try_asking")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-700 transition-colors hover:border-[#0052FF] hover:bg-blue-50 hover:text-[#0052FF]"
                      data-testid={`ai-suggestion-${s.slice(0, 10)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex items-end gap-2 border-t border-slate-200 bg-white p-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t("assistant.input_placeholder")}
              rows={1}
              className="max-h-24 min-h-[38px] flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0052FF] focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
              disabled={sending}
              data-testid="ai-assistant-input"
            />
            <Button
              onClick={send}
              disabled={sending || !input.trim()}
              className="h-[38px] rounded-md bg-[#0052FF] px-3 hover:bg-[#0040CC]"
              data-testid="ai-assistant-send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
